// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierPieces.jsx   ·   THE LANDMARKS
// ────────────────────────────────────────────────────────────────────
// The things on the boardwalk that are not a wing's shop: the lamps, the
// Crest Well, the Hall of Deeds, the two stalls, W'ily's post, the old
// sign, the gate and the thing at the Far End.
//
// ⚑ ONE COMPONENT PER LANDMARK, and each takes only `{ n }` — its row in
// INTERACTABLES — so `PIER_PIECES` in Pier3D is a plain id → component
// table. That table is the art swap point: replace a row, replace a
// landmark.
//
// Same laws as pierBuild.jsx: zero lights, merged by material, seeded,
// one driver for every animation, and anything tall registers with the
// camera fader.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { Merger, boxUV, canvasTex, mulberry32, textTex } from "./pierTex";
import { stoneTex, shingleTex, timberTex, dotTex, poolTex, shadowTex } from "./pierPaint";
import { Merged, BUILD_TICKS, useOccluder } from "./pierBuild";

const GOLD = "#c9a84c";
const GOLD_HI = "#f0d060";

function useTick(fn, deps) {
  useEffect(() => {
    BUILD_TICKS.add(fn);
    return () => { BUILD_TICKS.delete(fn); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function disposeGeos(g) { Object.values(g || {}).forEach((x) => x.dispose()); }

// A Points field from [{x,y,z,color,size}] — every halo of a landmark in one draw.
export function Glows({ points, size = 1, occ = null }) {
  const { geo, mat } = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(points.length * 3);
    const col = new Float32Array(points.length * 3);
    const c = new THREE.Color();
    points.forEach((p, i) => {
      pos.set([p.x, p.y, p.z], i * 3);
      c.set(p.color);
      const k = p.size || 1;           // brightness rides on colour for Points
      col.set([c.r * k, c.g * k, c.b * k], i * 3);
    });
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("color", new THREE.BufferAttribute(col, 3));
    const m = new THREE.PointsMaterial({
      size, map: dotTex(), vertexColors: true, transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, sizeAttenuation: true, toneMapped: false, fog: false,
    });
    return { geo: g, mat: m };
  }, [points, size]);
  useEffect(() => () => { geo.dispose(); mat.dispose(); }, [geo, mat]);
  // ⚠ a halo belongs to its structure: when the fader ghosts that, the
  // halo goes with it, or two lit dots hang in the air where a brazier was
  useTick(() => { mat.opacity = occ && occ.current ? occ.current.k : 1; }, [mat, occ]);
  return <points geometry={geo} material={mat} frustumCulled={false} />;
}

// ════════════════════════════════════════════════════════════════════
// THE LAMPPOSTS — every one on the pier, in four draws
// ════════════════════════════════════════════════════════════════════
export function Lampposts({ lamps }) {
  const built = useMemo(() => {
    const m = new Merger();
    const glows = [];
    for (const { x, y } of lamps) {
      m.cyl("iron", 0.15, 0.19, 0.22, 10, [x, 0.11, y]);
      m.cyl("iron", 0.09, 0.12, 0.16, 10, [x, 0.3, y]);
      m.cyl("iron", 0.045, 0.06, 2.2, 8, [x, 1.46, y]);
      m.cyl("iron", 0.08, 0.08, 0.06, 10, [x, 1.1, y]);
      m.cyl("iron", 0.11, 0.07, 0.1, 10, [x, 2.55, y]);
      m.box("glass", 0.22, 0.32, 0.22, [x, 2.78, y]);
      for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
        m.box("iron", 0.03, 0.34, 0.03, [x + dx * 0.115, 2.78, y + dz * 0.115]);
      }
      m.cyl("iron", 0, 0.2, 0.2, 4, [x, 3.04, y], [0, Math.PI / 4, 0]);
      m.cyl("iron", 0.02, 0.02, 0.14, 6, [x, 3.2, y]);
      m.plane("pool", 4.8, 4.8, [x, 0.015, y], [-Math.PI / 2, 0, 0]);
      glows.push({ x, y: 2.8, z: y, color: "#ffc978", size: 1 });
    }
    return { geos: m.build(), glows };
  }, [lamps]);

  const mats = useMemo(() => ({
    iron: new THREE.MeshStandardMaterial({ color: "#221c2c", roughness: 0.5, metalness: 0.7, emissive: "#1a1210", emissiveIntensity: 0.4 }),
    glass: new THREE.MeshBasicMaterial({ color: "#ffd89a", toneMapped: false }),
    pool: new THREE.MeshBasicMaterial({
      map: poolTex(), color: "#ffcf8a", transparent: true, opacity: 0.55, depthWrite: false,
      blending: THREE.AdditiveBlending, toneMapped: false,
    }),
  }), []);
  useEffect(() => () => disposeGeos(built.geos), [built]);

  return (
    <group>
      <Merged geos={built.geos} mats={mats} />
      <Glows points={built.glows} size={1.5} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE CREST WELL — the bank of the Empire, in dressed stone
// ────────────────────────────────────────────────────────────────────
// A round stone well with a gold-lit surface, four iron posts under a
// little pyramid roof, and the Crest itself turning over the water. Every
// currency in the Empire passes through here; it should look like it has
// been here longer than the pier.
// ════════════════════════════════════════════════════════════════════
const wellWaterTex = () => canvasTex("bw-wellwater", 256, (ctx, S) => {
  const c = S / 2;
  const g = ctx.createRadialGradient(c, c, 0, c, c, c);
  g.addColorStop(0, "#fff3c0"); g.addColorStop(0.35, GOLD_HI); g.addColorStop(0.8, "#7a5a18"); g.addColorStop(1, "#2a1c08");
  ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
  const R = mulberry32(0xCE57);
  ctx.globalCompositeOperation = "lighter";
  for (let i = 0; i < 7; i++) {
    ctx.beginPath(); ctx.arc(c, c, S * (0.1 + i * 0.06), R() * 6, R() * 6 + 2 + R() * 2);
    ctx.strokeStyle = "rgba(255,240,190,0.35)"; ctx.lineWidth = 2 + R() * 3; ctx.stroke();
  }
});

export function CrestWell({ n }) {
  const built = useMemo(() => {
    const m = new Merger();
    const { x, y } = n;
    m.cyl("stone", 1.0, 1.06, 0.78, 28, [x, 0.39, y], [0, 0, 0], true);
    m.cyl("stoneIn", 0.84, 0.84, 0.74, 28, [x, 0.39, y], [0, 0, 0], true);
    m.add("stone", new THREE.RingGeometry(0.8, 1.1, 28), [x, 0.79, y], [-Math.PI / 2, 0, 0]);
    m.cyl("stone", 1.12, 1.12, 0.1, 28, [x, 0.84, y], [0, 0, 0], true);
    m.cyl("stone", 1.14, 1.2, 0.14, 28, [x, 0.07, y]);
    for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const px = x + dx * 0.72, pz = y + dz * 0.72;
      m.cyl("iron", 0.05, 0.06, 1.6, 8, [px, 1.6, pz]);
      m.cyl("gold", 0.08, 0.08, 0.08, 8, [px, 0.92, pz]);
    }
    // ⚠ LOW ENOUGH TO SEE THE HALL'S DOOR OVER. At 3.7 tall, four tiles in
    // front of the Hall's steps and on its centre line, the roof covered the
    // door from the boardwalk (visual review, 2026-09-14). Now 3.1 tall.
    m.box("timber", 1.62, 0.1, 0.1, [x, 2.35, y - 0.72]);
    m.box("timber", 1.62, 0.1, 0.1, [x, 2.35, y + 0.72]);
    m.box("timber", 0.1, 0.1, 1.62, [x - 0.72, 2.35, y]);
    m.box("timber", 0.1, 0.1, 1.62, [x + 0.72, 2.35, y]);
    // ⚠ AN IRON CROWN, NOT A ROOF. Even lowered to 3.1, a shingled canopy on
    // the Hall's centre line covered the lower half of its doorway from the
    // boardwalk (second visual review). Four thin ribs keep the Well a
    // landmark and let the Hall's door read straight through it.
    const apex = new THREE.Vector3(x, 3.05, y), up = new THREE.Vector3(0, 1, 0);
    for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const from = new THREE.Vector3(x + dx * 0.72, 2.4, y + dz * 0.72);
      const d = apex.clone().sub(from), len = d.length();
      const e = new THREE.Euler().setFromQuaternion(new THREE.Quaternion().setFromUnitVectors(up, d.normalize()));
      m.box("iron", 0.05, len, 0.05, [(from.x + apex.x) / 2, (from.y + apex.y) / 2, (from.z + apex.z) / 2], [e.x, e.y, e.z]);
    }
    m.add("gold", new THREE.TorusGeometry(0.52, 0.035, 6, 24), [x, 2.72, y], [Math.PI / 2, 0, 0]);
    m.cyl("gold", 0.03, 0.05, 0.34, 6, [x, 3.2, y]);
    // ⚠ the Crest HANGS from the crown on a chain. With the roof gone it was a
    // gem floating in an empty cage (second visual review).
    m.cyl("iron", 0.014, 0.014, 1.1, 4, [x, 2.5, y]);
    m.add("gold", new THREE.TorusGeometry(0.05, 0.014, 6, 12), [x, 1.97, y]);
    // the winch beam and its chain, straight down to the water
    m.box("timber", 1.5, 0.08, 0.08, [x, 2.12, y]);
    m.cyl("iron", 0.012, 0.012, 1.35, 4, [x + 0.3, 1.5, y]);
    const geos = m.build();
    ["stone", "stoneIn", "timber", "roof"].forEach((k) => geos[k] && boxUV(geos[k], 1.4));
    return geos;
  }, [n]);

  const mats = useMemo(() => {
    const water = wellWaterTex().clone();
    water.center.set(0.5, 0.5);
    water.needsUpdate = true;
    return {
      stone: new THREE.MeshStandardMaterial({ map: stoneTex(), color: "#b8aec4", roughness: 0.95, side: THREE.DoubleSide }),
      stoneIn: new THREE.MeshStandardMaterial({ map: stoneTex(), color: "#3a3440", roughness: 1, side: THREE.BackSide }),
      iron: new THREE.MeshStandardMaterial({ color: "#221c2c", roughness: 0.5, metalness: 0.7 }),
      gold: new THREE.MeshStandardMaterial({ color: GOLD, emissive: "#6a4a10", emissiveIntensity: 0.6, metalness: 0.9, roughness: 0.3 }),
      timber: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#7a5c40", roughness: 1 }),
      roof: new THREE.MeshStandardMaterial({ map: shingleTex(), color: "#b09a6a", roughness: 0.9 }),
      water: new THREE.MeshBasicMaterial({ map: water, toneMapped: false }),
      crest: new THREE.MeshStandardMaterial({
        color: GOLD_HI, emissive: GOLD_HI, emissiveIntensity: 1.2, metalness: 0.8, roughness: 0.25, toneMapped: false,
      }),
      pool: new THREE.MeshBasicMaterial({
        map: poolTex(), color: GOLD_HI, transparent: true, opacity: 0.55, depthWrite: false,
        blending: THREE.AdditiveBlending, toneMapped: false,
      }),
    };
  }, []);
  useEffect(() => () => disposeGeos(built), [built]);

  const crest = useRef();
  const moteSeed = useMemo(() => {
    const R = mulberry32(0x3073);
    return Array.from({ length: 16 }, () => ({ a: R() * 6.28, r: R() * 0.7, s: 0.3 + R() * 0.5, p: R() }));
  }, []);
  const moteGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(moteSeed.length * 3), 3));
    return g;
  }, [moteSeed]);
  const moteMat = useMemo(() => new THREE.PointsMaterial({
    size: 0.16, map: dotTex(), color: GOLD_HI, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, toneMapped: false, fog: false,
  }), []);

  useTick((t) => {
    mats.water.map.rotation = t * 0.12;
    if (crest.current) {
      crest.current.rotation.y = t * 0.8;
    }
    const a = moteGeo.attributes.position;
    moteSeed.forEach((m, i) => {
      const k = (t * m.s * 0.35 + m.p) % 1;     // THE RISE — up, and gone
      a.setXYZ(i, n.x + Math.cos(m.a + t * 0.2) * m.r, 0.6 + k * 1.55, n.y + Math.sin(m.a + t * 0.2) * m.r);
    });
    a.needsUpdate = true;
    moteMat.opacity = 0.8;
  }, [mats, moteGeo, moteSeed, n.x, n.y]);

  const glows = useMemo(() => [
    { x: n.x, y: 0.9, z: n.y, color: GOLD_HI, size: 1.2 },
    { x: n.x, y: 1.55, z: n.y, color: GOLD_HI, size: 0.8 },
  ], [n.x, n.y]);

  return (
    <group>
      <Merged geos={built} mats={mats} />
      <mesh position={[n.x, 0.6, n.y]} rotation={[-Math.PI / 2, 0, 0]} material={mats.water}>
        <circleGeometry args={[0.84, 28]} />
      </mesh>
      <mesh ref={crest} position={[n.x, 1.55, n.y]} scale={[0.26, 0.4, 0.26]} material={mats.crest}>
        <octahedronGeometry args={[1, 0]} />
      </mesh>
      <mesh position={[n.x, 0.02, n.y]} rotation={[-Math.PI / 2, 0, 0]} material={mats.pool}>
        <planeGeometry args={[5.2, 5.2]} />
      </mesh>
      <points geometry={moteGeo} material={moteMat} frustumCulled={false} />
      <Glows points={glows} size={2.4} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE HALL OF DEEDS — the civic front of the pier
// ────────────────────────────────────────────────────────────────────
// A small temple in pale stone: three steps, six columns, a pediment with
// the name on its frieze, a gold-lit door, violet banners, and a brazier
// either side. It faces the Crest Well across the spine — the Empire's
// bank and the Empire's record, looking at each other.
// Same local frame as a shopfront: facade at z=0 facing +z.
// ════════════════════════════════════════════════════════════════════
export function HallOfDeeds({ front }) {
  const W = front.w - 0.2, D = 3.1;
  const built = useMemo(() => {
    const m = new Merger();
    // stylobate — three steps rising back from the deck edge
    m.box("stone", W + 0.5, 0.13, D + 0.3, [0, 0.065, -D / 2 + 0.05]);
    m.box("stone", W + 0.3, 0.13, D + 0.05, [0, 0.195, -D / 2 - 0.06]);
    m.box("stone", W + 0.1, 0.13, D - 0.2, [0, 0.325, -D / 2 - 0.18]);
    const base = 0.39;
    // the cella
    const cz = -0.95, cd = D - 1.2;
    m.box("stone", W - 0.5, 3.0, cd, [0, base + 1.5, cz - cd / 2]);
    // six columns across the front
    const colH = 2.9, colZ = -0.42;
    for (let i = 0; i < 6; i++) {
      const cx = -W / 2 + 0.35 + (i * (W - 0.7)) / 5;
      m.box("stone", 0.44, 0.12, 0.44, [cx, base + 0.06, colZ]);
      m.cyl("marble", 0.15, 0.17, colH, 14, [cx, base + 0.12 + colH / 2, colZ]);
      m.box("stone", 0.46, 0.14, 0.46, [cx, base + 0.12 + colH + 0.07, colZ]);
    }
    const eave = base + 0.12 + colH + 0.14;
    // entablature
    m.box("stone", W + 0.1, 0.52, D - 0.1, [0, eave + 0.26, -D / 2 - 0.1]);
    m.box("gold", W + 0.14, 0.05, 0.08, [0, eave + 0.03, colZ + 0.05 + 0.2]);
    // the pediment, ridge running front to back
    const rise = 1.0, spanW = W + 0.3, depth = D + 0.1;
    const top = eave + 0.52;
    m.add("stone", new THREE.CylinderGeometry(1, 1, 1, 3), [0, top + 0.5 * (rise / 1.5), -D / 2 - 0.1],
      [-Math.PI / 2, 0, 0], [spanW / 1.732, depth, rise / 1.5]);
    const a = Math.atan2(rise, spanW / 2);
    const L = Math.hypot(rise, spanW / 2) + 0.2;
    m.box("roof", L, 0.1, depth + 0.3, [-spanW / 4, top + rise / 2 + 0.06, -D / 2 - 0.1], [0, 0, a]);
    m.box("roof", L, 0.1, depth + 0.3, [spanW / 4, top + rise / 2 + 0.06, -D / 2 - 0.1], [0, 0, -a]);
    // the doorway in the cella
    m.box("dark", 1.3, 2.3, 0.12, [0, base + 1.15, cz + 0.02]);
    m.box("gold", 1.52, 0.12, 0.16, [0, base + 2.36, cz + 0.06]);
    for (const sx of [-1, 1]) m.box("gold", 0.1, 2.3, 0.14, [sx * 0.7, base + 1.15, cz + 0.05]);
    // braziers at the foot of the steps
    for (const sx of [-1, 1]) {
      const bx = sx * (W / 2 + 0.05);
      // ⚠ on the bottom step, not on the walk: at +0.35 they stood 0.15
      // from the seam lampposts (through them) and in the player's path
      m.box("stone", 0.34, 0.9, 0.34, [bx, 0.45, -0.18]);
      m.cyl("gold", 0.28, 0.12, 0.2, 12, [bx, 1.0, -0.18]);
    }
    const geos = m.build();
    ["stone", "roof", "marble"].forEach((k) => geos[k] && boxUV(geos[k], 0.9));
    return { geos, eave, colZ, cz, base, top, rise };
  }, [W]);

  const mats = useMemo(() => ({
    stone: new THREE.MeshStandardMaterial({ map: stoneTex(), color: "#d8d0e2", roughness: 0.9 }),
    marble: new THREE.MeshStandardMaterial({ color: "#e6e0ec", roughness: 0.6 }),
    roof: new THREE.MeshStandardMaterial({ map: shingleTex(), color: "#8a7aa8", roughness: 0.9 }),
    gold: new THREE.MeshStandardMaterial({ color: GOLD, emissive: "#6a4a10", emissiveIntensity: 0.7, metalness: 0.9, roughness: 0.3 }),
    dark: new THREE.MeshBasicMaterial({ color: "#07050c" }),
    doorGlow: new THREE.MeshBasicMaterial({
      map: poolTex(), color: GOLD_HI, transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, toneMapped: false, opacity: 0.9,
    }),
    frieze: new THREE.MeshBasicMaterial({
      map: textTex("HALL OF DEEDS", GOLD_HI, { font: "900 64px Cinzel, Palatino Linotype, serif" }),
      transparent: true, depthWrite: false, toneMapped: false,
    }),
    banner: new THREE.MeshStandardMaterial({ map: bannerTex(), side: THREE.DoubleSide, roughness: 0.9, emissive: "#2a1650", emissiveIntensity: 0.35 }),
    flame: new THREE.MeshBasicMaterial({
      map: poolTex(), color: "#ffb84d", transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, toneMapped: false,
    }),
    spill: new THREE.MeshBasicMaterial({
      map: poolTex(), color: GOLD_HI, transparent: true, opacity: 0.5, depthWrite: false,
      blending: THREE.AdditiveBlending, toneMapped: false,
    }),
  }), []);
  useEffect(() => () => disposeGeos(built.geos), [built]);

  const ref = useRef();
  const flames = useRef([]);
  useTick((t) => {
    flames.current.forEach((f, i) => {
      if (!f) return;
      const k = 1 + Math.sin(t * 7 + i * 2) * 0.12 + Math.sin(t * 13 + i) * 0.06;
      f.scale.set(0.55 * k, 0.9 * k, 1);
    });
  }, []);
  const mass = useMemo(() => new THREE.Box3(
    new THREE.Vector3(-W / 2 - 0.2, 0, -D), new THREE.Vector3(W / 2 + 0.2, built.top + built.rise, -0.02),
  ), [W, built]);
  const occ = useOccluder(ref, mass, [mats.stone, mats.marble, mats.roof, mats.gold, mats.dark, mats.banner, mats.frieze], [mass, mats]);
  // the additive layers fade here, with the stone
  useTick(() => {
    const k = occ.current ? occ.current.k : 1;
    mats.doorGlow.opacity = 0.9 * k;
    mats.flame.opacity = k;
    mats.spill.opacity = 0.5 * k;
  }, [mats, occ]);

  const rotY = front.face === "S" ? 0 : Math.PI;
  const { eave, colZ, cz, base } = built;
  const dir = front.face === "S" ? 1 : -1;
  const cx = front.x + front.w / 2;
  const glows = useMemo(() => [
    { x: cx - (W / 2 + 0.05) * dir, y: 1.25, z: front.y - 0.18 * dir, color: "#ffb84d", size: 1.2 },
    { x: cx + (W / 2 + 0.05) * dir, y: 1.25, z: front.y - 0.18 * dir, color: "#ffb84d", size: 1.2 },
    { x: cx, y: 1.4, z: front.y + (cz + 0.2) * dir, color: GOLD_HI, size: 1.4 },
  ], [cx, W, dir, front.y, cz]);

  return (
    <group>
      <group ref={ref} position={[cx, 0, front.y]} rotation={[0, rotY, 0]}>
        <Merged geos={built.geos} mats={mats} />
        <mesh position={[0, eave + 0.26, colZ + 0.28]} material={mats.frieze}>
          <planeGeometry args={[Math.min(W * 0.8, 4.2), Math.min(W * 0.8, 4.2) / 8]} />
        </mesh>
        <mesh position={[0, base + 1.15, cz + 0.1]} material={mats.doorGlow}>
          {/* inside the door frame, not a lit sheet over the stone around it */}
          <planeGeometry args={[1.2, 2.15]} />
        </mesh>
        {[-1, 1].map((sx) => (
          <mesh key={sx} position={[sx * (W / 2 - 0.9), eave - 0.85, colZ - 0.05]} material={mats.banner}>
            <planeGeometry args={[0.62, 1.5]} />
          </mesh>
        ))}
        {[-1, 1].map((sx, i) => (
          <mesh key={`f${sx}`} ref={(el) => { flames.current[i] = el; }}
                position={[sx * (W / 2 + 0.05), 1.42, -0.18]} material={mats.flame}>
            <planeGeometry args={[1, 1]} />
          </mesh>
        ))}
        <mesh position={[0, 0.02, 1.1]} rotation={[-Math.PI / 2, 0, 0]} material={mats.spill}>
          <planeGeometry args={[4.2, 2.6]} />
        </mesh>
      </group>
      <Glows points={glows} size={2.2} occ={occ} />
    </group>
  );
}

// A violet banner with the Crest on it.
const bannerTex = () => canvasTex("bw-banner", 128, (ctx, W, H) => {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#4a2a8a"); g.addColorStop(1, "#2a1450");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = GOLD; ctx.lineWidth = 5; ctx.strokeRect(8, 6, W - 16, H - 30);
  ctx.fillStyle = GOLD_HI;
  ctx.beginPath(); ctx.moveTo(W / 2, H * 0.22); ctx.lineTo(W * 0.72, H * 0.42); ctx.lineTo(W / 2, H * 0.62); ctx.lineTo(W * 0.28, H * 0.42); ctx.closePath(); ctx.fill();
  // swallowtail hem
  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath(); ctx.moveTo(0, H); ctx.lineTo(W / 2, H - 22); ctx.lineTo(W, H); ctx.closePath(); ctx.fill();
}, 256);

// ════════════════════════════════════════════════════════════════════
// ⭐ THE BOARDWALK FURNITURE — every bench, crate, barrel and planter in
// four draws, each standing in its own contact shadow.
// Data: PIER_PROPS in UnstablePierre.js. Local frame per piece: its length
// runs along x, its front faces +z, and `rot` turns it about y.
// ════════════════════════════════════════════════════════════════════
export function PierProps({ props }) {
  const geos = useMemo(() => {
    const m = new Merger();
    const R = mulberry32(0xF0B1);
    for (const p of props) {
      const th = p.rot || 0, c = Math.cos(th), s = Math.sin(th);
      const at = (ox, oy, oz) => [p.x + ox * c + oz * s, oy, p.y - ox * s + oz * c];
      const rot = (r = 0) => [0, th + r, 0];
      const big = p.kind === "tree" || p.kind === "anchor";
      m.plane("shadow", p.kind === "bench" ? 2.0 : big ? 2.8 : 1.4, big ? 2.8 : 1.4, [p.x, 0.012, p.y], [-Math.PI / 2, 0, th]);
      if (p.kind === "bench") {
        for (const ox of [-0.62, 0.62]) {
          m.box("iron", 0.06, 0.45, 0.42, at(ox, 0.225, 0), rot());
          m.box("iron", 0.06, 0.5, 0.06, at(ox, 0.7, -0.2), rot());
        }
        for (const oz of [-0.14, 0, 0.14]) m.box("timber", 1.5, 0.05, 0.11, at(0, 0.47, oz), rot());
        for (const oy of [0.66, 0.82]) m.box("timber", 1.5, 0.1, 0.05, at(0, oy, -0.22), rot());
      } else if (p.kind === "crates") {
        m.box("timber", 0.6, 0.55, 0.6, at(-0.2, 0.275, 0), rot(R() * 0.4));
        m.box("timber", 0.5, 0.45, 0.5, at(0.38, 0.225, 0.1), rot(-R() * 0.5));
        m.box("timber", 0.45, 0.4, 0.45, at(-0.15, 0.75, 0.02), rot(R() * 0.8));
      } else if (p.kind === "barrels") {
        for (const [ox, oz] of [[-0.3, 0], [0.32, 0.12]]) {
          m.cyl("timber", 0.27, 0.3, 0.75, 14, at(ox, 0.375, oz));
          for (const oy of [0.16, 0.59]) m.cyl("iron", 0.31, 0.31, 0.05, 14, at(ox, oy, oz));
        }
      } else if (p.kind === "planter") {
        m.box("timber", 1.1, 0.42, 0.5, at(0, 0.21, 0), rot());
        m.add("leaf", new THREE.SphereGeometry(0.5, 10, 8), at(-0.2, 0.55, 0), rot(), [1, 0.55, 0.6]);
        m.add("leaf", new THREE.SphereGeometry(0.42, 10, 8), at(0.25, 0.58, 0.02), rot(), [1, 0.6, 0.6]);
      } else if (p.kind === "anchor") {
        // a ship's anchor stood on a stone plinth — the square's centrepiece
        m.cyl("stone", 0.72, 0.8, 0.36, 8, at(0, 0.18, 0), rot(Math.PI / 8));
        m.cyl("stone", 0.5, 0.56, 0.24, 8, at(0, 0.48, 0), rot(Math.PI / 8));
        m.box("iron", 0.15, 2.0, 0.15, at(0, 1.6, 0), rot());                                  // shank
        m.box("iron", 1.0, 0.12, 0.12, at(0, 2.3, 0), rot());                                  // stock
        m.add("iron", new THREE.TorusGeometry(0.19, 0.05, 6, 14), at(0, 2.78, 0), rot());      // ring
        m.add("iron", new THREE.TorusGeometry(0.62, 0.08, 6, 16, Math.PI), at(0, 1.24, 0), [0, th, Math.PI]);  // arms
        for (const sx of [-1, 1]) m.add("iron", new THREE.ConeGeometry(0.17, 0.4, 4), at(sx * 0.62, 1.36, 0), [0, th, -sx * 0.5]);
      } else if (p.kind === "scope") {
        // a coin telescope on a post, always looking east, out to sea
        m.cyl("iron", 0.17, 0.24, 0.12, 10, at(0, 0.06, 0));
        m.cyl("iron", 0.05, 0.065, 1.05, 8, at(0, 0.62, 0));
        m.box("iron", 0.24, 0.2, 0.22, at(0, 1.2, 0));                                        // the coin box
        m.add("brass", new THREE.CylinderGeometry(0.075, 0.11, 0.66, 12), at(0.14, 1.4, 0), [0, 0, -(Math.PI / 2 - 0.25)]);
        m.add("brass", new THREE.CylinderGeometry(0.05, 0.05, 0.12, 10), at(-0.22, 1.31, 0), [0, 0, -(Math.PI / 2 - 0.25)]);
      } else if (p.kind === "tree") {
        // a round planter with a bench seat on its rim and a small sea-pine
        m.cyl("stone", 0.95, 1.0, 0.48, 18, at(0, 0.24, 0));
        m.add("timber", new THREE.RingGeometry(0.78, 1.08, 20), at(0, 0.5, 0), [-Math.PI / 2, 0, 0]);
        m.cyl("leaf", 0.78, 0.78, 0.1, 14, at(0, 0.5, 0));
        m.cyl("timber", 0.08, 0.13, 1.6, 7, at(0, 1.25, 0));
        m.add("leaf", new THREE.SphereGeometry(0.78, 10, 8), at(0, 2.3, 0), rot(), [1, 0.78, 1]);
        m.add("leaf", new THREE.SphereGeometry(0.5, 9, 7), at(-0.42, 1.98, 0.2), rot(), [1, 0.8, 1]);
        m.add("leaf", new THREE.SphereGeometry(0.52, 9, 7), at(0.4, 2.04, -0.18), rot(), [1, 0.8, 1]);
      }
    }
    const g = m.build();
    if (g.timber) boxUV(g.timber, 1.4);
    if (g.stone) boxUV(g.stone, 1.2);
    return g;
  }, [props]);
  const mats = useMemo(() => ({
    timber: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#9a7a56", roughness: 1 }),
    iron: new THREE.MeshStandardMaterial({ color: "#241e2c", roughness: 0.5, metalness: 0.6 }),
    stone: new THREE.MeshStandardMaterial({ map: stoneTex(), color: "#9a90a4", roughness: 1 }),
    brass: new THREE.MeshStandardMaterial({ color: "#b8913a", metalness: 0.8, roughness: 0.35, emissive: "#3a2a08", emissiveIntensity: 0.4 }),
    leaf: new THREE.MeshStandardMaterial({ color: "#46703e", roughness: 0.9, flatShading: true, emissive: "#0e1a0c", emissiveIntensity: 0.4 }),
    shadow: new THREE.MeshBasicMaterial({ map: shadowTex(), transparent: true, depthWrite: false, opacity: 0.7 }),
  }), []);
  useEffect(() => () => disposeGeos(geos), [geos]);
  useEffect(() => () => Object.values(mats).forEach((mt) => mt.dispose()), [mats]);
  return <Merged geos={geos} mats={mats} />;
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE INLAYS — the boardwalk is designed, not merely planked
// ────────────────────────────────────────────────────────────────────
// Once the pier had room to walk in, the room read as a bare field of
// boards. The answer is not more things to walk round — that is how it
// got cramped — but FLOOR: a brass compass rose set into the boardwalk
// where each pair of squares opens onto it, a smaller one in the middle
// of every square that has no landmark, and a border of pale boards
// running along both edges of the main walk. All of it is flush with the
// deck: nothing to collide with, nothing narrowing a lane.
// ════════════════════════════════════════════════════════════════════
const roseTex = (big) => canvasTex(`bw-rose|${big ? 1 : 0}`, 512, (ctx, S) => {
  const c = S / 2;
  ctx.clearRect(0, 0, S, S);
  // the brass ring
  ctx.lineWidth = S * 0.035; ctx.strokeStyle = "#c9a84c";
  ctx.beginPath(); ctx.arc(c, c, S * 0.44, 0, 7); ctx.stroke();
  ctx.lineWidth = S * 0.012; ctx.strokeStyle = "#8a6520";
  ctx.beginPath(); ctx.arc(c, c, S * 0.39, 0, 7); ctx.stroke();
  // the inlaid dark field
  const g = ctx.createRadialGradient(c, c, 0, c, c, S * 0.39);
  g.addColorStop(0, "rgba(40,24,16,0.55)"); g.addColorStop(1, "rgba(20,12,8,0.35)");
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(c, c, S * 0.385, 0, 7); ctx.fill();
  // tick marks
  for (let i = 0; i < 32; i++) {
    const a = (i / 32) * Math.PI * 2, r0 = S * (i % 4 ? 0.405 : 0.39), r1 = S * 0.43;
    ctx.strokeStyle = "#c9a84c"; ctx.lineWidth = i % 4 ? 2 : 4;
    ctx.beginPath(); ctx.moveTo(c + Math.cos(a) * r0, c + Math.sin(a) * r0); ctx.lineTo(c + Math.cos(a) * r1, c + Math.sin(a) * r1); ctx.stroke();
  }
  // the points of the rose: four long, four short
  const point = (a, len, w, col) => {
    ctx.fillStyle = col; ctx.beginPath();
    ctx.moveTo(c + Math.cos(a) * len, c + Math.sin(a) * len);
    ctx.lineTo(c + Math.cos(a + Math.PI / 2) * w, c + Math.sin(a + Math.PI / 2) * w);
    ctx.lineTo(c, c);
    ctx.closePath(); ctx.fill();
    ctx.fillStyle = "rgba(0,0,0,0.25)"; ctx.beginPath();
    ctx.moveTo(c + Math.cos(a) * len, c + Math.sin(a) * len);
    ctx.lineTo(c + Math.cos(a - Math.PI / 2) * w, c + Math.sin(a - Math.PI / 2) * w);
    ctx.lineTo(c, c);
    ctx.closePath(); ctx.fill();
  };
  for (let i = 0; i < 4; i++) point((i / 4) * Math.PI * 2 + Math.PI / 4, S * 0.24, S * 0.035, "#a88a50");
  for (let i = 0; i < 4; i++) point((i / 4) * Math.PI * 2 - Math.PI / 2, S * 0.36, S * 0.05, "#e0c070");
  // the crest at the heart
  ctx.fillStyle = "#f0d060";
  ctx.beginPath(); ctx.moveTo(c, c - S * 0.06); ctx.lineTo(c + S * 0.04, c); ctx.lineTo(c, c + S * 0.06); ctx.lineTo(c - S * 0.04, c); ctx.closePath(); ctx.fill();
  if (big) {
    ctx.font = `900 ${S * 0.05}px Cinzel, 'Palatino Linotype', serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillStyle = "#e0c070";
    ctx.fillText("N", c, c - S * 0.47 + S * 0.075);
  }
});

// ⚠ LOW CONTRAST ON PURPOSE. The first cut was two pale boards with dark
// edges, and from the street camera each read as a beam or a step lying
// ACROSS the walk — an obstacle drawn on the very lane that was just
// cleared. An inlay is a slightly warmer run of boards and a line of brass
// nails, close to the deck's own tone, and nothing else.
const NO_SQUARES = [];
const SHOW_ROSES = false;
export function PierInlays({ world }) {
  const squares = world.squares || NO_SQUARES;
  const spine = world.spine;
  const built = useMemo(() => {
    const m = new Merger();
    const roses = [];
    if (spine) {
      const midY = (spine.y0 + spine.y1) / 2;
      const seenX = new Set();
      for (const s of squares) {
        const cx = (s.x0 + s.x1) / 2;
        if (!seenX.has(cx)) { seenX.add(cx); roses.push({ x: cx, y: midY, r: 2.1, big: true }); }
      }
      // the border: a thin brass line along each edge of the main walk,
      // BROKEN at every square's mouth. ⚠ A full-width band of boards read as
      // a kerb lying across each square's opening (visual review, 2026-09-14).
      const bw = 0.07;
      for (const [y, side] of [[spine.y0 - 0.5 + 0.9, "N"], [spine.y1 + 0.5 - 0.9, "S"]]) {
        const cuts = squares.filter((s) => s.side === side).map((s) => [s.x0 - 0.5, s.x1 + 0.5]).sort((a, b) => a[0] - b[0]);
        let from = -0.5;
        for (const [c0, c1] of [...cuts, [world.cols - 0.5, world.cols - 0.5]]) {
          if (c0 - from > 0.2) m.plane("border", c0 - from, bw, [(from + c0) / 2, 0.004, y], [-Math.PI / 2, 0, 0]);
          from = c1;
        }
      }
    }
    // a smaller rose in every square with nothing standing in its middle
    const occupied = [
      ...(world.interactables || []).filter((n) => n.solid),
      ...(world.props || []).filter((p) => p.kind === "anchor" || p.kind === "tree"),
    ];
    for (const s of squares) {
      const cx = (s.x0 + s.x1) / 2, cy = (s.y0 + s.y1) / 2;
      if (!occupied.some((n) => Math.hypot(n.x - cx, n.y - cy) < 3)) roses.push({ x: cx, y: cy, r: 1.5, big: false });
    }
    const geos = m.build();
    return { geos, roses };
  }, [squares, spine, world.cols, world.interactables, world.props]);

  const mats = useMemo(() => {
    const deckPolish = { polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 };
    return {
      border: new THREE.MeshStandardMaterial({ color: "#c9a84c", metalness: 0.7, roughness: 0.35, emissive: "#3a2a08", emissiveIntensity: 0.45, ...deckPolish }),
      roseBig: new THREE.MeshStandardMaterial({ map: roseTex(true), transparent: true, depthWrite: false, roughness: 0.4, metalness: 0.5, emissive: "#3a2a08", emissiveIntensity: 0.25, ...deckPolish }),
      rose: new THREE.MeshStandardMaterial({ map: roseTex(false), transparent: true, depthWrite: false, roughness: 0.4, metalness: 0.5, emissive: "#3a2a08", emissiveIntensity: 0.25, ...deckPolish }),
    };
  }, []);
  useEffect(() => () => disposeGeos(built.geos), [built]);
  useEffect(() => () => Object.values(mats).forEach((mt) => mt.dispose()), [mats]);

  return (
    <group>
      <Merged geos={built.geos} mats={mats} />
      {/* ⚑ caP'n 2026-09-25: "get rid of all those compass things for now everywhere" —
          off, not deleted. Set SHOW_ROSES true to bring them back. */}
      {SHOW_ROSES && built.roses.map((r, i) => (
        <mesh key={i} position={[r.x, 0.008, r.y]} rotation={[-Math.PI / 2, 0, 0]} material={r.big ? mats.roseBig : mats.rose} renderOrder={1}>
          <planeGeometry args={[r.r * 2, r.r * 2]} />
        </mesh>
      ))}
    </group>
  );
}
